import React from 'react';
import { PenLine, Clapperboard, Store } from 'lucide-react';

/**
 * THE PATH FROM A STORY TO A SALE, in three steps (owner 2026-09-12: *"we are too high level... they create
 * micro-drama with our platform, we help them set up their platform to sell it through our platform"*).
 *
 * WHY A HOW-IT-WORKS IS BACK, having been deliberately removed on 2026-08-24. The version that was cut was a
 * pipeline diagram plus a nine-department crew claim — it explained how WE work, which is not a question a
 * visitor has. The difference here is the third step. Shopify closes its homepage on *Add your first product
 * → Customize your store → **Set up payments*** — the money step lives INSIDE the how-it-works rather than
 * behind it, so a would-be seller can see the whole path before committing. Etsy does the same by publishing
 * its fee table on the seller page. A creator who is here to earn should not have to sign up to find out
 * whether earning is possible.
 *
 * WHAT IT MUST NOT DO IS OVERSELL. Episodes are watchable and a viewer can unlock one, but creators are paid
 * in CREDITS and there is no cash payout rail yet. So step three promises a storefront and a price, never a
 * payday — writing a cheque the product cannot cash is exactly what the audience this page targets will check.
 */
const STEPS = [
  {
    icon: PenLine,
    title: 'Write it',
    body: 'One line of an idea becomes episodes: beats, dialogue and score, by your AI writer and director.',
  },
  {
    icon: Clapperboard,
    title: 'Shoot it',
    body: 'Your crew renders every shot with the same faces and sets from episode one to the last. Draft cheap, keep the best takes.',
  },
  {
    icon: Store,
    title: 'Sell it',
    body: 'Your own store address. Give the opening episodes away; on the Plus plan, charge for the rest and keep 90%.',
  },
];

/**
 * `compact` is the landing HERO's form (LAND-1, owner 2026-09-26: *"keep it direct, concise, clear what we offer,
 * and what is in it for user"*): no section heading, tighter type, so the offer, the path and the sign-in all sit
 * above the fold beside the reel. The full form is kept for any page that argues the path on its own.
 */
const SellingSteps: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <section className={compact ? '' : 'px-4 pb-12'} aria-labelledby={compact ? undefined : 'how-it-works'} aria-label={compact ? 'How it works' : undefined}>
    <div className={compact ? '' : 'mx-auto max-w-4xl'}>
      {!compact && (
        <h2
          id="how-it-works"
          className="text-center text-2xl font-semibold tracking-tight text-secondary-900 dark:text-white sm:text-3xl"
        >
          From an idea to something you can sell
        </h2>
      )}
      {/* Numbered because it IS a sequence — each step needs the one before it. Structure that encodes
          something true about the content, rather than decoration. */}
      <ol className={compact ? 'grid gap-5 sm:grid-cols-3' : 'mt-8 grid gap-6 sm:grid-cols-3'}>
        {STEPS.map((step, i) => (
          <li key={step.title} className={`flex flex-col gap-1.5 ${compact ? 'border-t border-secondary-200 pt-3 dark:border-secondary-800' : ''}`}>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[11px] text-secondary-400 dark:text-secondary-500">
                {String(i + 1).padStart(2, '0')}
              </span>
              <step.icon className="h-4 w-4 shrink-0 text-secondary-500 dark:text-secondary-400" aria-hidden />
              <h3 className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-secondary-900 dark:text-white`}>{step.title}</h3>
            </div>
            <p className={`${compact ? 'text-[13px]' : 'text-sm'} leading-relaxed text-secondary-500 dark:text-secondary-400`}>{step.body}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export default SellingSteps;
